import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Select, { SingleValue } from 'react-select'

import {
  ChannelHelper,
  http,
  useCrud,
  usePaginationSearchParams,
  useQueryParams,
} from '@monorepo/infra'
import { Pagination as PaginationType } from '@monorepo/interfaces'
import { IReactSelectOption, Resource } from '@monorepo/interfaces'
import { SearchTerm } from '../../molecules'

import { DashboardBackButton, PaginationDetails } from '../../atoms'
import {
  Pagination,
  ResourcesGallery,
  SectionResourcesList,
  TabList,
  SectionUsers,
} from '../../../components'

import * as S from './styles'

type ResourceFetch = PaginationType & {
  data: Resource[]
}

const channelOptions = ChannelHelper.getChannelOptions()

// The list view's channel filter also offers "all" (no filter); the preview
// is always scoped to one channel because that's how dealers see it.
const ALL_CHANNELS_OPTION: IReactSelectOption = {
  value: '',
  label: 'All channels',
}
const listChannelOptions = [ALL_CHANNELS_OPTION, ...channelOptions]

const Resources = (): JSX.Element => {
  const resourceBaseURL = '/resource'
  const pageURLKey = 'p'
  const searchURLKey = 's'

  const queryParams = useQueryParams()

  const [searchTerm, setSearchTerm] = useState(
    queryParams.get(searchURLKey) ?? ''
  )
  const history = useHistory()

  // 'list' (default) is the classic admin table — with status tabs, a channel
  // filter, and server-side search. 'preview' renders resources exactly as
  // dealers see them on the storefront (per channel, active only) with an
  // Edit overlay on each card.
  const [view, setView] = useState<'preview' | 'list'>('list')

  // ----- dealer preview state -----
  const [channelOption, setChannelOption] = useState<IReactSelectOption>(
    channelOptions[0]
  )
  const [previewData, setPreviewData] = useState<Resource[]>([])
  const [previewLoading, setPreviewLoading] = useState(true)

  // Load EVERY page of the dealer view up front — same fix as the storefront
  // page: the previous infinite-scroll approach silently stalled for some
  // users and hid everything past the first pages (always the newest
  // content, since the API returns oldest-first). The set is small (<100
  // docs), so a full fetch is cheap and always complete.
  useEffect(() => {
    let active = true

    const fetchAllPages = async () => {
      setPreviewLoading(true)

      const allPages: Resource[] = []

      try {
        let pageNum = 1
        let totalPages = 1

        do {
          const url = `/resource/channel?channel=${channelOption.value}${
            pageNum > 1 ? `&pageNum=${pageNum}` : ''
          }`
          const response = await http.get({ url })
          const result = response?.data as {
            data?: Resource[]
            totalPages?: number | string
          }

          if (Array.isArray(result?.data)) {
            allPages.push(...result.data)
          }

          totalPages = Number(result?.totalPages) || 1
          ++pageNum
        } while (pageNum <= totalPages)
      } catch (error) {
        // Keep whatever pages we did get — a partial list beats a blank page.
        console.log(error)
      }

      if (active) {
        const unique: Resource[] = []
        allPages.forEach((item) => {
          if (
            unique.findIndex((existing) => existing._id === item._id) === -1
          ) {
            unique.push(item)
          }
        })
        setPreviewData(unique)
        setPreviewLoading(false)
      }
    }

    fetchAllPages()

    return () => {
      active = false
    }
  }, [channelOption])

  const handlePreviewChannelChange = (
    newValue: SingleValue<IReactSelectOption>
  ) => {
    if (newValue) {
      setChannelOption(newValue as IReactSelectOption)
    }
  }

  // In preview mode search filters the loaded cards client-side (the dealer
  // endpoint has no title search); the list view keeps full server search.
  const trimmedSearch = searchTerm.trim().toLowerCase()
  const filteredPreviewData = trimmedSearch
    ? previewData.filter((resource) =>
        `${resource.title} ${resource.description}`
          .toLowerCase()
          .includes(trimmedSearch)
      )
    : previewData

  // ----- classic list state -----
  const [listChannelOption, setListChannelOption] =
    useState<IReactSelectOption>(ALL_CHANNELS_OPTION)

  const { apiSearchParams, handlePageChange, handlePrevPage, handleNextPage } =
    usePaginationSearchParams({
      pageAPIKey: 'pageNum',
      pageURLKey,
      searchAPIKey: 'title',
      searchURLKey,
      searchTerm,
    })

  const resourceSearchParams = new URLSearchParams(apiSearchParams)

  if (resourceSearchParams.get('status') === 'all') {
    resourceSearchParams.delete('status')
  }

  // Channel filter for the list view ("" = all channels, param omitted)
  if (listChannelOption.value) {
    resourceSearchParams.set('channel', listChannelOption.value)
  }

  const apiURL = `${resourceBaseURL}?${resourceSearchParams}`

  const { fetch } = useCrud<Resource>(apiURL, '_id', {
    revalidateOnFocus: false,
  })

  const { data, loading } = fetch
  const { data: resources, ...pagination } = data as ResourceFetch
  pagination.page = Number(pagination.page)

  const handleListChannelChange = (
    newValue: SingleValue<IReactSelectOption>
  ) => {
    if (newValue) {
      setListChannelOption(newValue as IReactSelectOption)
    }
  }

  const handleCreateResource = () => history.push('resources/create')

  const handleEditResource = (id: string) =>
    history.push(`/admin/resources/edit/${id}`)

  const handleClearSearchTerm = () => setSearchTerm('')

  return (
    <S.Main>
      <DashboardBackButton redirectURL="/admin" text="Back to Admin" />
      <SectionUsers title="Resources" />
      <S.PageActions>
        {view === 'list' ? (
          <TabList
            tabKey="status"
            defaultTabName="active"
            tabs={[
              { description: 'All', tabName: 'all' },
              { description: 'Active', tabName: 'active' },
              // Inactive + Archived merged into one "Expired" bucket (AK).
              // The backend 'expired' filter matches both legacy statuses.
              { description: 'Expired', tabName: 'expired' },
            ]}
          />
        ) : (
          <S.PreviewBar>
            <S.ChannelSelectContainer>
              <Select
                name="previewChannel"
                options={channelOptions}
                value={channelOption}
                onChange={handlePreviewChannelChange}
                isSearchable={false}
                components={{ IndicatorSeparator: () => null }}
              />
            </S.ChannelSelectContainer>
            <S.PreviewHint>
              Showing active resources for {channelOption.label}
            </S.PreviewHint>
          </S.PreviewBar>
        )}
        <S.ActionButtonsContainer>
          <S.ViewToggleContainer>
            <S.ViewToggleButton
              $active={view === 'list'}
              onClick={() => setView('list')}
              title="Manage all resources"
            >
              LIST
            </S.ViewToggleButton>
            <S.ViewToggleButton
              $active={view === 'preview'}
              onClick={() => setView('preview')}
              title="Preview the storefront resources page"
            >
              PREVIEW
            </S.ViewToggleButton>
          </S.ViewToggleContainer>
          <S.CreateResourceButton
            label="Create Resource"
            colorOption="blue"
            onClick={handleCreateResource}
          />
        </S.ActionButtonsContainer>
      </S.PageActions>
      <S.Separator />
      <S.SearchFiltersContainer>
        <S.SearchGroup>
          <S.Search
            placeholder="Search Resources"
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
          />
          {view === 'list' ? (
            <S.ChannelSelectContainer>
              <Select
                name="listChannel"
                options={listChannelOptions}
                value={listChannelOption}
                onChange={handleListChannelChange}
                isSearchable={false}
                components={{ IndicatorSeparator: () => null }}
              />
            </S.ChannelSelectContainer>
          ) : undefined}
        </S.SearchGroup>
        <SearchTerm searchTerm={searchTerm} onClear={handleClearSearchTerm} />
        {view === 'list' ? (
          <PaginationDetails
            totalItems={pagination.totalCount}
            totalPages={pagination.totalPages}
            itemsPerPage={pagination.pageSize}
            pageNumber={pagination.page}
            onClickPrev={handlePrevPage}
            onClickNext={handleNextPage}
          />
        ) : undefined}
      </S.SearchFiltersContainer>
      {view === 'preview' ? (
        <ResourcesGallery
          resources={filteredPreviewData}
          loading={previewLoading}
          onEdit={handleEditResource}
        />
      ) : (
        <>
          <SectionResourcesList loading={loading} resources={resources} />
          <S.PaginationContainer>
            <Pagination
              onPageChange={handlePageChange}
              totalCount={pagination.totalCount}
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalPages={pagination.totalPages}
            />
          </S.PaginationContainer>
        </>
      )}
    </S.Main>
  )
}

export default Resources
